import React, { useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button, IconButton, useToast, Spinner } from '@chakra-ui/react';
import { Breadcrumb, RippleButton, Title } from '../../Components/Atoms';
import { StoreContext } from '../../Library/contexts';
import { getDrivers, getRoutes, getRouteStats } from '../../APIs';
import PATHS from '../../Routes/paths';
import { DownloadIcon, EditModelIcon, ArrowRightIcon } from '../../Assets/images/svgs';
import "./style.css";
import { DatePicker } from '../../Components/Molecules';
import { getISODate, secondsToHms, stampToDate } from '../../Library/datetime';
import { DATE_RANGES } from '../../Library/constants';
import { jsonToCSVConvertor } from '../../Library/jsonUtils';

const DriverRouteSummary = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const driverId = searchParams.get("idx");

    const { filters, userVehicles, allVehicleRoutes, setAllVehicleRoutes, driverManagementCache, setDriverManagementCache } = useContext(StoreContext);
    const [driverDetails, setDriverDetails] = useState(null);
    const [driverRoutes, setDriverRoutes] = useState([]);
    const [processedStats, setProcessedStats] = useState(null);
    const [loading, setLoading] = useState(false);


    const breadcrumbPaths = [
        { idx: 1, name: "Driver Management", link: PATHS.DRIVER_MANAGEMENT },
        { idx: 2, name: driverDetails?.nm || "Driver Details", isCurrentPage: true },
    ];

    // Fetch Driver Details — read from cache populated by DriverManagement list page;
    // fall back to a network fetch only if the cache is empty (e.g. direct URL access).
    useEffect(() => {
        if (!driverId) return;
        const cached = driverManagementCache.subUsers;
        if (cached && cached.length) {
            setDriverDetails(cached.find(d => String(d.idx) === String(driverId)) || null);
            return;
        }
        getDrivers((err, data) => {
            if (err) console.error(err);
            else setDriverDetails((data || []).find(d => String(d.idx) === String(driverId)) || null);
        });
    }, [driverId]);


    const handleEditDriver = () => {
        navigate(`${PATHS.DRIVER_MANAGEMENT}/${PATHS.DRIVER_MANAGEMENT_ADD}?idx=${driverId}&isEdit=true`);
    };

    const vehicleTripDets = [
        { label: "Routes", key: "routeCount" },
        { label: "Total Trips", key: "tripCount" },
        { label: "Expected Route Dist.", key: "totalExpectedDist", dist: true },
        { label: "Actual Route Dist.", key: "totalActualDist", dist: true },
        { label: "Deviated Dist.", key: "totalDeviatedDist", dist: true, deviated: true },
        { label: "Total Distance", key: "totalTravelledDist", dist: true  },
        { label: "Total Duration", key: "totalTravelledDur", ts: true },
        { label: "Running Duration", key: "totalRunningDur", ts: true  },
        { label: "Avg Speed", key: "averageSpd", speed: true },
        { label: "Top Speed", key: "topSpd", speed: true },
    ];

    const getDisplayDurWoSec = (ts) => {
        let timeObj = secondsToHms(ts, true);
        return `${timeObj.h} h ${timeObj.m} m`;
    }

    const processFetchedStats = (routeStats, relevantRoutes) => {
        const finalSummary = routeStats.reduce((acc, { dayStats, routes = [], vid }) => {
            routes.forEach((route) => {
                let { routeId, trips, routeDst, dst, deviated, dur, tripStats } = route;

                // FILTER BY DRIVER
                let driverTrips = 0;
                let driverDst = 0;
                let driverDev = 0;
                let driverDur = 0;

                if (tripStats && Array.isArray(tripStats)) {
                    tripStats.forEach(trip => {
                        if (String(trip.driverId) === String(driverId)) {
                            driverTrips += 1;
                            driverDst += (trip.dst || 0);
                            driverDev += (trip.deviated || 0);
                            driverDur += (trip.dur || trip.duration || 0);
                        }
                    });
                }

                if (driverTrips === 0) return;

                if (!acc.routeMap[routeId]) {
                    acc.routeMap[routeId] = { routeId, trips: 0, routeDst: 0, dst: 0, deviated: 0, dur: 0, vid: vid };
                }
                const r = acc.routeMap[routeId];
                r.trips += driverTrips;
                r.routeDst += (routeDst * driverTrips);
                r.dst += driverDst;
                r.deviated += driverDev;
                r.dur += driverDur;
                
                // Track VID for button (last one wins if multiple)
                r.vid = vid;

                acc.totalTripCount += driverTrips;
                acc.totalExpectedDist += (routeDst * driverTrips);
                acc.totalActualDist += driverDst;
                acc.totalRouteDur += driverDur;
                acc.totalDeviatedDist += driverDev;
                
                acc.totalTravelledDist += driverDst;
                acc.totalTravelledDur += driverDur;
                acc.totalRunningDur += driverDur; // Using duration as proxy for running duration
            });

            return acc;
        }, {
            routeMap: {}, totalTripCount: 0, totalExpectedDist: 0, totalActualDist: 0,
            totalDeviatedDist: 0, totalTravelledDist: 0, totalTravelledDur: 0,
            totalRunningDur: 0, topSpd: 0
        });

        const mergedRoutes = Object.values(finalSummary.routeMap);

        // Calculate Average Speed (km/h) = (Total Dist (m) / Total Time (s)) * 3.6
        // Avoiding division by zero
        let avgSpd = 0;
        if (finalSummary.totalTravelledDur > 0) {
            avgSpd = ((finalSummary.totalTravelledDist / finalSummary.totalTravelledDur) * 3.6).toFixed(1);
        }

        const summaryObject = {
            routeCount: mergedRoutes.length,
            tripCount: finalSummary.totalTripCount,
            totalExpectedDist: finalSummary.totalExpectedDist,
            totalActualDist: finalSummary.totalActualDist,
            totalDeviatedDist: finalSummary.totalDeviatedDist,
            totalTravelledDist: finalSummary.totalTravelledDist,
            totalTravelledDur: finalSummary.totalTravelledDur,
            totalRunningDur: finalSummary.totalRunningDur,
            averageSpd: avgSpd,
            topSpd: 0, // Not available per-driver per-trip currently
            mergedRoutes: mergedRoutes
        };
        setProcessedStats(summaryObject);
        
        // Map names and find RegNo
        let routeNameMap = {};
        relevantRoutes.forEach(route => {
            routeNameMap[route.idx] = route.nm;
        });
        
        // Helper to find RegNo from all master vehicles
        const getRegNo = (vid) => {
            const veh = (userVehicles || []).find(v => v.idx === vid);
            return veh ? veh.reg : "-";
        };

        let mergedRoutesWithName = summaryObject.mergedRoutes.map(route => ({ 
            ...route, 
            nm: routeNameMap[route.routeId],
            reg: getRegNo(route.vid)
        }));
        setDriverRoutes(mergedRoutesWithName);
    }

    // Fetch Routes and Stats
    useEffect(() => {
        if (!driverDetails) return;

        const fetchDriverData = () => {
             // Use all master-account vehicles (driver record no longer has vehicle list)
             const allVehicleIds = new Set((userVehicles || []).map(v => v.idx));

             // 2. Fetch All Routes to filter relevant ones
             const onRoutesFetched = (routes) => {
                 const relevantRoutes = routes.filter(route => !route.archived);

                 if (allVehicleIds.size === 0) {
                     setProcessedStats({ mergedRoutes: [] });
                     setLoading(false);
                     return;
                 }

                 let selectedDate = DATE_RANGES[filters.range];
                 let vidsToFetch = Array.from(allVehicleIds);

                        const cacheKey = `${driverId}_${filters.range}`;
                        if (driverManagementCache.summary?.[cacheKey] && driverManagementCache.summary[cacheKey].length) {
                             processFetchedStats(driverManagementCache.summary[cacheKey], relevantRoutes);
                             setLoading(false);
                             return;
                        }

                 getRouteStats({ fromTs: selectedDate.from, toTs: selectedDate.to, vids: vidsToFetch }, (err, routeStats) => {
                    setLoading(false);
                    if (err) console.log(err);
                    else {
                        // Filter stats to only include relevant route IDs
                        const activeRouteIdxSet = new Set(relevantRoutes.map(r => r.idx));
                        const filteredRouteStats = routeStats.map(stat => {
                            const filteredRoutes = stat.routes.filter(r => activeRouteIdxSet.has(r.routeId));
                             return { ...stat, routes: filteredRoutes };
                        }).filter(stat => stat.routes.length > 0);

                        setDriverManagementCache(prev => ({
                            ...prev,
                            summary: {
                                ...prev.summary,
                                [cacheKey]: filteredRouteStats
                            }
                        }));
                        processFetchedStats(filteredRouteStats, relevantRoutes);
                    }
                 }, []);
             };

             if (allVehicleRoutes && allVehicleRoutes.length) {
                 setLoading(true);
                 onRoutesFetched(allVehicleRoutes);
             } else {
                 setLoading(true);
                 getRoutes((err, data) => {
                     if (err) {
                         console.error(err);
                         setLoading(false);
                     }
                     else {
                         setAllVehicleRoutes(data);
                         // onRoutesFetched(data); // Removed to prevent double call: useEffect will re-run when allVehicleRoutes updates
                     }
                 });
             }
        };

        fetchDriverData();

    }, [driverDetails, filters.range, allVehicleRoutes, setAllVehicleRoutes]);


    const routeCardDetails = [
        { label: "No. of Trips", key: "trips" },
        { label: "Expected Route Dist.", key: "routeDst", dst: true },
        { label: "Actual Route Dist.", key: "dst", dst: true },
        { label: "Deviated Dist.", key: "deviated", dst: true, deviated: true },
        { label: "Total Duration", key: "dur", ts: true },
    ];

    const handleReportDownload = () => {
        const getdst = (dst)=> {return `${(dst/1000).toFixed(1)} kms`};
        let dur = 0;
        var sendToExcel = driverRoutes.map(a => Object.assign({}, a));
        sendToExcel.forEach((stat, idx) => {
          stat["Sr No"] = idx + 1;
          stat["Driver Name"] = driverDetails?.nm || "-";
          stat["Trips"] = stat.trips || "-";
          stat["Expected Route Distance"] = stat.routeDst ? getdst(stat.routeDst) : "-";
          stat["Actual Route Distance"] = stat.dst ? getdst(stat.dst) : "-";
          stat["Deviated Distance"] = stat.deviated ? getdst(stat.deviated) : "-";
          stat["Total Duration"] = stat.dur ? secondsToHms(stat.dur) : "-";
          dur += stat.dur;
        });
    
        sendToExcel.push({ "Sr No": "", "Driver Name": "", "Trips": "", "Expected Route Distance": "", "Actual Route Distance": "", "Deviated Distance": "", "Total Duration": "" });
        // Corrected total summary row to match columns
        sendToExcel.push({ "Sr No": "", "Driver Name": "Total", "Trips": processedStats.tripCount, "Expected Route Distance": getdst(processedStats.totalExpectedDist), "Actual Route Distance": getdst(processedStats.totalActualDist), "Deviated Distance": getdst(processedStats.totalDeviatedDist), "Total Duration": secondsToHms(dur) });
    
        var sendData = sendToExcel.map(function (exceldata) {
          var args = "Sr No, Driver Name, Trips, Expected Route Distance, Actual Route Distance, Deviated Distance, Total Duration";
          var filteredData = args.split(",").reduce((mem, arg) => {
            arg = arg.trim();
            mem[arg] = exceldata[arg];
            return mem;
          }, {});
          return filteredData;
        });
        if (sendData.length) jsonToCSVConvertor(sendData, "", true, `Driver_Routes_Report_` + stampToDate(DATE_RANGES[filters.range].from) + "_" + stampToDate(DATE_RANGES[filters.range].to), false);
    }

    return (
        <section className="panel-main driver-route-summary">
             <div className="driver-summary-header">
                <Breadcrumb paths={breadcrumbPaths} />
                <DatePicker customMaxDate={getISODate()} />
             </div>

             {/* Driver Details Card */}
             <div className="driver-details-card">
                 <div className="driver-card-header">
                     <Title>Driver Details</Title>
                     <Button 
                        variant="outline" 
                        leftIcon={<EditModelIcon />} 
                        onClick={handleEditDriver}
                        className="edit-driver-btn"
                     >
                        Edit
                     </Button>
                 </div>
                 <div className="driver-info-grid">
                     <div className="driver-info-item">
                         <span className="driver-label">Driver Name</span>
                         <span className="driver-value">{driverDetails?.nm || "-"}</span>
                     </div>
                     <div className="driver-info-item">
                         <span className="driver-label">Driver PIN</span>
                         <span className="driver-value driver-pin-display">{driverDetails?.pin || "-"}</span>
                     </div>
                 </div>
             </div>

             { /* Copied from VehicleRouteSummary and Adapted */ }
             {loading ? (
                <div className="drs-loader-container" style={{ width: '100%', height: '300px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Spinner size="xl" />
                </div>
             ) : (processedStats ? <>
                <div className="vehicle-route-data-card">
                <div className="vehicle-route-data-card-header">
                    <p>Driver Route Statistics</p>
                    <RippleButton onClick={() => handleReportDownload()}>
                    <DownloadIcon />
                    Download Report
                    </RippleButton>
                </div>
                <div className="vehicle-route-details">
                    {vehicleTripDets.map(det => (
                    <div className="route-det-container">
                        <p className="route-det-label">{det.label || "-"}</p>
                        <p className={`route-det-value ${det.deviated ? "highlight-val" : ""}`}>{det.ts ? getDisplayDurWoSec(processedStats[det.key] || 0) : (det.dist ? (processedStats[det.key]/1000).toFixed(1) : processedStats[det.key] || 0)} {det.dist ? "kms" : (det.speed ? "km/hr" : "")}</p>
                    </div>
                    ))}
                </div>
                </div>

                {driverRoutes && driverRoutes.length ? <div className="vehicle-travelled-routes-container">
                <div className="vehicle-routes-header">
                    <Title>
                    Routes
                    </Title>
                </div>
                <div className="vehicle-route-cards-container">
                    {driverRoutes.map(route => (
                    <div className="vehicle-route-card">
                        <div className="veh-route-card-header">
                        <p className="veh-route-name">{route.nm || "-"}</p>
                        </div>
                        <div className="veh-route-details-container">
                        {routeCardDetails.map(dets => (
                            <div className="route-detail-box">
                            <p className="route-detail-label">{dets.label || "-"}</p>
                            <p className={`route-detail-value ${dets.deviated ? "highlight-text" : ""}`}>{dets.ts ? getDisplayDurWoSec(route[dets.key]) : (dets.dst ? (route[dets.key]/1000).toFixed(1) : route[dets.key]) || "-"} {dets.dst ? "kms" : ""}</p>
                            </div>
                        ))}
                        </div>
                        <div className="view-trip-btn-container">
                            <RippleButton variant={"unstyled"} className="veh-route-view-trip-btn" onClick={() => navigate(`${PATHS.DRIVER_MANAGEMENT}/${PATHS.DRIVER_TRIPS}?driverId=${driverId}&routeId=${route.routeId}`)}>
                                View trip details
                                <ArrowRightIcon />
                            </RippleButton>
                        </div>
                    </div>
                    ))}
                </div>
                </div> : null}
             </> : null)}
        </section>
    );
};

export default DriverRouteSummary;
